import { captureException } from '@sentry/browser'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import { generatePath } from 'react-router-dom'

import {
    acceptInvitationFailure,
    acceptInvitationRequest,
    acceptInvitationSuccess,
    acceptSignUpFormInvitationFailure,
    acceptSignUpFormInvitationRequest,
    acceptSignUpFormInvitationSuccess,
    changeOrganizationFailure,
    changeOrganizationGroupFailure,
    changeOrganizationGroupRequest,
    changeOrganizationRequest,
    createAuthPageOrganizationFailure,
    createAuthPageOrganizationRequest,
    createAuthPageOrganizationSuccess,
    fetchProfileFeaturePermissionsSuccess,
    impersonationModeFailure,
    loadAuthFinish,
    setUserIsImpersonating,
} from 'actions/auth'
import {
    fetchOrganizationGroupSuccess,
    fetchOrganizationSuccess,
} from 'actions/orgs'
import { getProfileFeaturePermissions } from 'api/Cerebro/orgScoped/amazonAdsApi'
import {
    createOrganization,
    getOrganizationGroup,
    NewOrganization,
} from 'api/Cerebro/orgScoped/organizationApi'
import { CerebroResourceResponse } from 'api/Cerebro/types'
import {
    getOrganization,
    patchInvitation,
} from 'api/Cerebro/unscoped/organization'
import { getCurrentUser } from 'api/Cerebro/unscoped/user'
import {
    cerebroApiRequest,
    cerebroApiRequestGuarded,
} from 'api/cerebroApiRequest'
import { SegmentEventName } from 'const/monitoring/segment'
import { beginBootstrapContext } from 'features/App/service/bootstrapContext'
import { identifyUserInPlanHat } from 'features/Monitoring/PlanHat'
import {
    getIdentifyProps,
    sendSegmentTrackAndIdentifyEvents,
} from 'features/Monitoring/Segment/service'
import { getPath, tryGetPath } from 'routing/pagePaths'
import { navigate } from 'routing/RoutingService'
import {
    selectDomainValue as selectAuthDomainValue,
    selectProfileFeaturePermissions,
} from 'selectors/auth'
import { Organization, ProfilePermission, User } from 'types'

import {
    fetchCurrentUser,
    fetchPendingInvitations,
    fetchUserOrganizationFeaturePermissions,
    getActiveOrganizationAndGroup,
    saveCurrentUserToApp,
    setActiveOrganization,
} from './bootstrap'
import { fetchOrganizationGroups } from './organizationGroups'
import { dispatch, getReduxState } from './reduxBridge'
import { fetchUserSettings, updateUserSettings } from './userSettings'

interface ChangeOrgPayload {
    organizationId: string
    redirect?: string
    tab?: string
    resources?: any
}

function resolveRedirectPath(
    redirect: string | undefined,
    resources: any,
    tab: string | undefined
): string | undefined {
    if (!redirect) {
        return undefined
    }
    try {
        let pathToRedirect = generatePath(tryGetPath(redirect), resources)
        pathToRedirect = tab ? `${pathToRedirect}?tab=${tab}` : pathToRedirect
        return pathToRedirect
    } catch (e) {
        console.error('Error generating path', e)
        return undefined
    }
}

export async function changeOrganization(
    payload: ChangeOrgPayload
): Promise<void> {
    dispatch(changeOrganizationRequest(payload))

    // Supersede any in-flight switch/bootstrap: only the latest transition's
    // writes commit, so a rapid second switch can't be overwritten by this
    // run's stale, out-of-context results.
    const shouldCommit = beginBootstrapContext()

    const { organizationId, redirect, resources, tab } = payload

    try {
        const userSettings = await fetchUserSettings(shouldCommit)

        const [{ data: user }, { data: organization }] = await Promise.all([
            getCurrentUser(),
            getOrganization(organizationId),
            fetchUserOrganizationFeaturePermissions({
                organizationId,
                shouldCommit,
            }),
            updateUserSettings(
                [{ path: ['organizationId'], value: organizationId }],
                shouldCommit
            ),
        ])
        if (shouldCommit()) {
            dispatch(fetchOrganizationSuccess(organization))
        }

        await Promise.all([
            sendSegmentTrackAndIdentifyEvents(
                SegmentEventName.ORG_CHANGED,
                {},
                userSettings.gilId ?? '',
                user.id,
                getIdentifyProps(
                    user,
                    userSettings.gilId,
                    organization.salesforce_id
                )
            ),
            identifyUserInPlanHat(
                organization.salesforce_id,
                userSettings.gilId
            ),
        ])

        if (!shouldCommit()) {
            return
        }

        const pathToRedirect = resolveRedirectPath(redirect, resources, tab)
        navigate(pathToRedirect ?? getPath('homePage'), { replace: true })

        // Dynamic import so the auth-service graph doesn't statically depend on
        // the app-service graph (which imports auth's userSettings/integrations)
        // — that cycle trips the build's CircularDependencyPlugin.
        const { bootstrapApp } = await import('features/App/appService')
        await bootstrapApp(shouldCommit)
    } catch (err: any) {
        captureException(err)
        if (shouldCommit()) {
            dispatch(changeOrganizationFailure({ error: err.message }))
        }
    }
}

export async function changeOrganizationGroup(payload: {
    organizationId: string
    organizationGroupId: string
    redirect?: string
    tab?: string
    resources?: any
}): Promise<void> {
    dispatch(changeOrganizationGroupRequest(payload))

    // Supersede any in-flight switch/bootstrap: only the latest transition's
    // writes commit (see `changeOrganization`).
    const shouldCommit = beginBootstrapContext()

    const { organizationId, organizationGroupId, redirect, resources, tab } =
        payload

    try {
        const [userSettings, { data: user }, { data: organization }] =
            await Promise.all([
                fetchUserSettings(shouldCommit),
                getCurrentUser(),
                getOrganization(organizationId),
                fetchUserOrganizationFeaturePermissions({
                    organizationGroupId,
                    shouldCommit,
                }),
                fetchOrganizationGroups(organizationId, shouldCommit),
            ])
        if (shouldCommit()) {
            dispatch(fetchOrganizationSuccess(organization))
        }
        await cerebroApiRequestGuarded(
            shouldCommit,
            fetchOrganizationGroupSuccess,
            getOrganizationGroup,
            organizationGroupId
        )

        await Promise.all([
            sendSegmentTrackAndIdentifyEvents(
                SegmentEventName.ORG_GROUP_CHANGED,
                {},
                userSettings.gilId ?? '',
                user.id,
                getIdentifyProps(
                    user,
                    userSettings.gilId,
                    organization.salesforce_id
                )
            ),
            identifyUserInPlanHat(
                organization.salesforce_id,
                userSettings.gilId
            ),
        ])

        if (!shouldCommit()) {
            return
        }

        const pathToRedirect = resolveRedirectPath(redirect, resources, tab)
        navigate(pathToRedirect ?? getPath('homePage'), { replace: true })

        // Dynamic import so the auth-service graph doesn't statically depend on
        // the app-service graph (which imports auth's userSettings/integrations)
        // — that cycle trips the build's CircularDependencyPlugin.
        const { bootstrapApp } = await import('features/App/appService')
        await bootstrapApp(shouldCommit)
    } catch (err: any) {
        captureException(err)
        if (shouldCommit()) {
            dispatch(changeOrganizationGroupFailure({ error: err.message }))
        }
    }
}

export async function createAuthPageOrganization(
    payload: NewOrganization
): Promise<void> {
    dispatch(createAuthPageOrganizationRequest(payload))

    try {
        const { data: organization }: CerebroResourceResponse<Organization> =
            await cerebroApiRequest(null, createOrganization, payload)

        await setActiveOrganization(organization.id)
        dispatch(createAuthPageOrganizationSuccess(organization))
    } catch (err: any) {
        dispatch(createAuthPageOrganizationFailure({ error: err.message }))
    }
}

/**
 * Accept an invitation from the invited-user sign-up form.
 */
export async function acceptSignUpFormInvitation({
    invitationId,
}: {
    invitationId: string
}): Promise<void> {
    dispatch(acceptSignUpFormInvitationRequest({ invitationId }))

    try {
        const organizationId: string = selectAuthDomainValue(getReduxState(), [
            'invitation',
            'organization',
            'id',
        ])

        await cerebroApiRequest(null, patchInvitation, invitationId, {
            state: 'accepted',
        })

        await cerebroApiRequest(
            fetchOrganizationSuccess,
            getOrganization,
            organizationId
        )

        await setActiveOrganization(organizationId)

        dispatch(acceptSignUpFormInvitationSuccess())
    } catch (err: any) {
        dispatch(acceptSignUpFormInvitationFailure({ error: err.message }))
    }
}

/**
 * Accept an invitation from the organization menu.
 */
export async function acceptInvitation({
    invitationId,
    callback = noop,
}: {
    invitationId: string
    callback?: () => void
}): Promise<void> {
    dispatch(acceptInvitationRequest({ invitationId }))

    try {
        const email: string = selectAuthDomainValue(getReduxState(), 'email')

        await cerebroApiRequest(null, patchInvitation, invitationId, {
            state: 'accepted',
        })

        await fetchPendingInvitations(email)

        dispatch(acceptInvitationSuccess())

        callback()
    } catch (err: any) {
        dispatch(acceptInvitationFailure({ error: err.message }))
    }
}

export async function fetchProfileFeaturePermissions({
    profileId,
}: {
    profileId: string
}): Promise<void> {
    const permissions: ProfilePermission[] = selectProfileFeaturePermissions(
        getReduxState(),
        profileId
    )

    if (isEmpty(permissions)) {
        const result: CerebroResourceResponse<ProfilePermission[]> =
            await cerebroApiRequest(
                null,
                getProfileFeaturePermissions,
                profileId
            )

        dispatch(
            fetchProfileFeaturePermissionsSuccess({
                profileId,
                permissions: result.data,
            })
        )
    }
}

/**
 * Enter or exit impersonation mode. The impersonation flag is written to the
 * store first (matching the saga's `setUserIsImpersonating` reducer path), then
 * the app is re-bootstrapped for the new context.
 */
export async function enterImpersonationMode(
    isImpersonating: boolean
): Promise<void> {
    dispatch(setUserIsImpersonating(isImpersonating))

    try {
        const response: CerebroResourceResponse<User> | null =
            await fetchCurrentUser()

        if (response === null || response.status !== 200) {
            dispatch(loadAuthFinish({}))
            return
        }

        const { data: currentUser } = response
        const userSettingsPromise = fetchUserSettings()

        const impersonating: boolean = selectAuthDomainValue(
            getReduxState(),
            'userIsImpersonating'
        )

        const { organization, organizationGroup, userSettings } =
            await getActiveOrganizationAndGroup(
                impersonating,
                userSettingsPromise
            )

        // Save user. `organization` may be null here; dereferencing it throws
        // (caught below → impersonationModeFailure), matching the saga.
        const activeOrganization = organization as Organization

        await Promise.all([
            fetchOrganizationGroups(activeOrganization.id),
            fetchUserOrganizationFeaturePermissions({
                organizationId: activeOrganization.id,
                organizationGroupId: organizationGroup?.id,
            }),
            saveCurrentUserToApp({
                currentUser,
                organization,
                organizationGroup,
                gilId: userSettings.gilId,
            }),
        ])

        navigate(getPath('homePage'))

        dispatch(loadAuthFinish({}))
    } catch (err: any) {
        captureException(err)
        dispatch(impersonationModeFailure({ error: err.message }))
        throw err
    }
}
